import { useEffect, useMemo, useState } from "react";
import { Loader2, Copy, Globe, Check, Clock } from "lucide-react";
import { api } from "../../lib/api";
import { useTenant } from "../../contexts/TenantContext";
import { toast } from "sonner";
import ImageUploader from "../../components/ImageUploader";
import { DELIVERY_ETA_PRESETS } from "../../lib/deliveryEta";

// Canonical branding fields the backend whitelists. Anything outside
// this set is silently dropped server-side; we mirror the whitelist
// here so legacy nested ``branding.branding`` corruption can't sneak
// back into the body on save.
const BRANDING_KEYS = [
    "logo_url", "customer_logo_url", "rider_logo_url",
    "splash_url", "favicon_url",
    "primary_hsl", "accent_hsl",
    "app_name", "tagline", "powered_by_text",
    "body_font", "heading_font",
    "theme_mode", "theme_preset",
    "custom_primary_hex",
    // iter83 — Browse landing routing.
    "home_tiles_enabled", "default_browse_mode",
    // iter114 — Discover-More section visibility.
    "discovery_mode_visible",
    // Whether shop owners and admins may use the Customer View.
    "staff_customer_view_enabled",
];

// iter83 — defaults for fields whose absence shouldn't coerce to "".
const BRANDING_DEFAULTS = {
    home_tiles_enabled: true,
    default_browse_mode: "AUTO",
    // iter114 — default ON so existing tenants keep the Discover-More
    // section until an admin explicitly hides it.
    discovery_mode_visible: true,
    // Default ON — existing tenants keep today's behaviour until an admin turns it off.
    staff_customer_view_enabled: true,
};

export default function AdminBranding() {
    const { tenant, applyTheme } = useTenant();
    const [busy, setBusy] = useState(false);
    const [b, setB] = useState(() => {
        // Hydrate from the loaded tenant — but strip any legacy nested
        // ``branding`` key so we don't echo the corruption back to the
        // server on save. Migrations heal the DB; this guards the wire.
        const src = tenant?.branding || {};
        const out = {};
        for (const k of BRANDING_KEYS) {
            const fallback = BRANDING_DEFAULTS[k] ?? "";
            out[k] = src[k] === undefined ? fallback : src[k];
        }
        return out;
    });
    // iter130 — Stock-urgency threshold lives at tenant-root (not inside
    // ``branding``), and has a dedicated PATCH endpoint. Keep its state
    // separate so a Branding save doesn't accidentally overwrite it.
    const [lowStockThreshold, setLowStockThreshold] = useState(
        tenant?.low_stock_threshold ?? 10,
    );
    const [savingStock, setSavingStock] = useState(false);
    // iter131 — Delivery-fee bearer (tenant-wide default).
    const [deliveryBearer, setDeliveryBearer] = useState(
        (tenant?.delivery_fee_borne_by || "ADMIN").toUpperCase(),
    );
    const [savingBearer, setSavingBearer] = useState(false);

    // iter134q — Delivery-ETA display override. Hydrates from
    // ``GET /admin/tenant-settings`` so we always show the persisted
    // state (not just what happens to be on the cached tenant doc).
    const [etaEnabled, setEtaEnabled] = useState(true);
    const [etaText, setEtaText] = useState("");
    const [etaPreset, setEtaPreset] = useState("");   // "" | preset value | "__custom__"
    const [savingEta, setSavingEta] = useState(false);
    const [etaLoaded, setEtaLoaded] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/tenant-settings");
                const enabled = data?.delivery_eta_display_enabled !== false;
                const text = (data?.delivery_eta_display_text || "").trim();
                setEtaEnabled(enabled);
                setEtaText(text);
                // Match the preset when text equals one of the curated
                // options; otherwise treat it as a custom entry.
                const knownValues = DELIVERY_ETA_PRESETS.map((p) => p.value);
                setEtaPreset(text === "" ? "" : (knownValues.includes(text) ? text : "__custom__"));
            } catch (err) {
                console.debug("[AdminBranding] tenant-settings load failed", err);
            } finally {
                setEtaLoaded(true);
            }
        })();
    }, []);

    const saveEta = async () => {
        setSavingEta(true);
        try {
            await api.patch("/admin/tenant-settings", {
                delivery_eta_display_enabled: etaEnabled,
                delivery_eta_display_text: etaText.trim(),
            });
            toast.success(etaEnabled
                ? (etaText.trim()
                    ? `Delivery ETA now shows "${etaText.trim()}"`
                    : "Delivery ETA reverted to per-shop minutes")
                : "Delivery ETA hidden from the storefront");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save ETA setting");
        } finally {
            setSavingEta(false);
        }
    };

    const save = async () => {
        setBusy(true);
        try {
            // Whitelist again at send time — paranoid defence vs a
            // future setB caller accidentally introducing a stray key.
            const payload = Object.fromEntries(
                BRANDING_KEYS.map((k) => [k, b[k] ?? ""])
                    .filter(([, v]) => v !== undefined),
            );
            const { data } = await api.patch("/admin/branding", payload);
            applyTheme?.(data.branding);
            toast.success("Branding saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const saveLowStock = async () => {
        const n = parseInt(lowStockThreshold, 10);
        if (isNaN(n) || n < 1 || n > 999) {
            toast.error("Please enter a whole number between 1 and 999");
            return;
        }
        setSavingStock(true);
        try {
            await api.patch("/admin/settings/low-stock-threshold", { value: n });
            toast.success(`Low-stock warning set to ${n}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save threshold");
        } finally {
            setSavingStock(false);
        }
    };

    const saveBearer = async (val) => {
        setSavingBearer(true);
        try {
            await api.patch("/admin/settings/delivery-fee-borne-by", { value: val });
            setDeliveryBearer(val);
            toast.success(
                val === "MERCHANT"
                    ? "Shop will now bear the delivery fee"
                    : "Admin will now bear the delivery fee",
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save");
        } finally {
            setSavingBearer(false);
        }
    };

    return (
        <div>
            <h1 className="text-2xl font-bold">Branding</h1>
            <p className="text-sm text-muted-foreground mt-1">
                Customize how your tenant looks in the web storefront and the two mobile shells.
                Upload separate logos for the Customer and Rider apps — each one is auto-resized into the
                <b> 512×512 launcher icon</b> and the <b>1024×500 Play Store feature graphic</b> during build.
            </p>

            <ShareURLCard tenant={tenant} />
            <p className="text-xs text-muted-foreground max-w-3xl mt-2">
                <b>Logo tip:</b> upload a square PNG at <b>1024×1024</b> (or larger) —
                that's the source we use as-is for the app icon and splash screen on
                Android and iOS. Smaller uploads still work but may look soft on
                high-density screens.
            </p>
            <div className="mt-6 bg-card border border-border rounded-2xl p-5 max-w-3xl space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-4 items-start">
                    <ImageUploader
                        label="Storefront Logo"
                        value={b.logo_url}
                        onUploaded={(url) => setB({ ...b, logo_url: url })}
                        variant="logo"
                        aspect="aspect-square"
                        testId="brand-logo-upload"
                    />
                    <div className="space-y-3">
                        <F label="App Name">
                            <input
                                data-testid="brand-app-name"
                                className="inp"
                                value={b.app_name || ""}
                                onChange={(e) => setB({ ...b, app_name: e.target.value })}
                            />
                        </F>
                        <F label="Tagline">
                            <input
                                className="inp"
                                value={b.tagline || ""}
                                onChange={(e) => setB({ ...b, tagline: e.target.value })}
                            />
                        </F>
                        <F label='"Powered by" — brand name only'>
                            <input
                                data-testid="brand-powered-by-text"
                                className="inp"
                                placeholder="Bizil"
                                value={b.powered_by_text || ""}
                                onChange={(e) => setB({ ...b, powered_by_text: e.target.value })}
                            />
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Enter only the brand name (e.g. <span className="font-semibold">Bizil</span>).
                                The footer will render it as
                                <span className="font-semibold"> "Powered by {b.powered_by_text?.replace(/^powered\s+by\s+/i, "").trim() || "Bizil"}"</span>.
                                Do not include the words "Powered by" in this field.
                            </p>
                        </F>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border" data-testid="brand-app-logos">
                    <div className="bg-muted/40 rounded-2xl p-4">
                        <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Customer App</p>
                        <p className="text-xs text-muted-foreground mt-0.5 mb-3">Used by the customer-facing mobile shell. PNG with transparent background works best.</p>
                        <ImageUploader
                            label="Customer App Logo (1024×1024)"
                            value={b.customer_logo_url || b.logo_url}
                            onUploaded={(url) => setB({ ...b, customer_logo_url: url })}
                            variant="logo"
                            aspect="aspect-square"
                            testId="brand-customer-logo-upload"
                        />
                        {!b.customer_logo_url && b.logo_url && (
                            <p className="text-[10px] text-amber-700 mt-2">Currently using the storefront logo as a fallback.</p>
                        )}
                    </div>
                    <div className="bg-muted/40 rounded-2xl p-4">
                        <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Rider App</p>
                        <p className="text-xs text-muted-foreground mt-0.5 mb-3">Used by the rider mobile shell — keep it distinct so riders spot their app in the launcher.</p>
                        <ImageUploader
                            label="Rider App Logo (1024×1024)"
                            value={b.rider_logo_url || b.logo_url}
                            onUploaded={(url) => setB({ ...b, rider_logo_url: url })}
                            variant="logo"
                            aspect="aspect-square"
                            testId="brand-rider-logo-upload"
                        />
                        {!b.rider_logo_url && b.logo_url && (
                            <p className="text-[10px] text-amber-700 mt-2">Currently using the storefront logo as a fallback.</p>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <F label="Primary HSL">
                        <input
                            className="inp"
                            data-testid="brand-primary"
                            value={b.primary_hsl || ""}
                            onChange={(e) => setB({ ...b, primary_hsl: e.target.value })}
                            placeholder="142 71% 45%"
                        />
                    </F>
                    <F label="Accent HSL">
                        <input
                            className="inp"
                            value={b.accent_hsl || ""}
                            onChange={(e) => setB({ ...b, accent_hsl: e.target.value })}
                            placeholder="39 100% 50%"
                        />
                    </F>
                </div>

                {/* iter83 — Browse landing routing. */}
                <div className="border-t border-border pt-5">
                    <h3 className="font-bold text-sm">Storefront landing &amp; browse layout</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Decide what customers see when they open <code>/</code> and how they browse a vertical.
                    </p>
                    <div className="mt-4 space-y-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="branding-home-tiles-toggle"
                                checked={!!b.home_tiles_enabled}
                                onChange={(e) => setB({ ...b, home_tiles_enabled: e.target.checked })}
                                className="mt-1 accent-primary"
                            />
                            <span className="text-sm">
                                <span className="font-semibold">Show the Home Tiles landing page</span>
                                <span className="block text-xs text-muted-foreground mt-0.5">
                                    When off, opening the storefront lands the customer directly on the default browse page below.
                                </span>
                            </span>
                        </label>
                        <F label="Default browse layout">
                            <select
                                data-testid="branding-default-browse-mode"
                                className="inp"
                                value={b.default_browse_mode || "AUTO"}
                                onChange={(e) => setB({ ...b, default_browse_mode: e.target.value })}
                            >
                                <option value="AUTO">Auto (use theme preset)</option>
                                <option value="SHOPS">Browse by Shops (Swiggy-style)</option>
                                <option value="PRODUCTS">Browse by Products (Flipkart-style)</option>
                            </select>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Used when a Home Tile&apos;s landing mode is AUTO, and when Home Tiles is disabled.
                            </p>
                        </F>
                        {/* iter114 — Discover-More section visibility. */}
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="branding-discovery-mode-visible"
                                checked={b.discovery_mode_visible !== false}
                                onChange={(e) => setB({ ...b, discovery_mode_visible: e.target.checked })}
                                className="mt-1 accent-primary"
                            />
                            <span className="text-sm">
                                <span className="font-semibold">Show the Discover More section</span>
                                <span className="block text-xs text-muted-foreground mt-0.5">
                                    Horizontal categories carousel that appears below the hero tiles on Home. When off, the section and its data are skipped entirely.
                                </span>
                            </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="branding-staff-customer-view"
                                checked={b.staff_customer_view_enabled !== false}
                                onChange={(e) => setB({ ...b, staff_customer_view_enabled: e.target.checked })}
                                className="mt-1 accent-primary"
                            />
                            <span className="text-sm">
                                <span className="font-semibold">Show Customer View to Shop Owners and Admins</span>
                                <span className="block text-xs text-muted-foreground mt-0.5">
                                    When off, shop owners and admins always open in their own console and the Customer option is hidden from their role switcher. Customers are not affected.
                                </span>
                            </span>
                        </label>
                    </div>
                </div>

                <button
                    data-testid="save-branding"
                    onClick={save}
                    disabled={busy}
                    className="bg-primary text-primary-foreground rounded-xl px-5 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-60"
                >
                    {busy && <Loader2 size={16} className="animate-spin" />} Save & Apply
                </button>
            </div>

            {/* iter130 — Stock urgency threshold */}
            <div className="mt-6 bg-card border border-border rounded-2xl p-5 max-w-3xl">
                <h3 className="font-bold text-sm">Stock Urgency Indicator</h3>
                <p className="text-xs text-muted-foreground mt-1">
                    When a product&apos;s remaining stock drops to or below this number,
                    the storefront shows an <span className="font-semibold">&quot;Only N left&quot;</span> pill
                    on product cards and detail pages to nudge the customer toward checkout.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3 max-w-md">
                    <F label="Low-stock warning threshold">
                        <input
                            type="number"
                            min="1"
                            max="999"
                            step="1"
                            data-testid="low-stock-threshold-input"
                            className="inp"
                            value={lowStockThreshold}
                            onChange={(e) => setLowStockThreshold(e.target.value)}
                            placeholder="10"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Enter a whole number between 1 and 999. Default is 10.
                        </p>
                    </F>
                    <button
                        data-testid="save-low-stock-threshold"
                        onClick={saveLowStock}
                        disabled={savingStock}
                        className="bg-primary text-primary-foreground rounded-xl px-5 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-60 shrink-0"
                    >
                        {savingStock && <Loader2 size={16} className="animate-spin" />} Save
                    </button>
                </div>
            </div>

            {/* iter131 — Delivery-fee bearer (tenant-wide default) */}
            <div className="mt-6 bg-card border border-border rounded-2xl p-5 max-w-3xl" data-testid="delivery-bearer-section">
                <h3 className="font-bold text-sm">Delivery Fee — Who Bears It?</h3>
                <p className="text-xs text-muted-foreground mt-1">
                    Choose who absorbs the rider&apos;s delivery payout on every order. This is the tenant-wide default —
                    individual shops can override this from the shop edit page. Existing orders are not re-computed.
                </p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                        type="button"
                        data-testid="delivery-bearer-admin"
                        disabled={savingBearer}
                        onClick={() => saveBearer("ADMIN")}
                        className={`text-left rounded-2xl border p-4 transition ${
                            deliveryBearer === "ADMIN"
                                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                : "border-border hover:border-primary/40"
                        } disabled:opacity-60`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-bold text-sm">Admin (Platform)</span>
                            {deliveryBearer === "ADMIN" && (
                                <span className="text-[10px] uppercase tracking-wider font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">Active</span>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1.5">
                            The platform absorbs the rider fee. Shop payout stays untouched by delivery costs.
                            <span className="block mt-1 text-[11px]">Recommended for tenants that promise free/subsidised delivery to customers.</span>
                        </p>
                    </button>
                    <button
                        type="button"
                        data-testid="delivery-bearer-merchant"
                        disabled={savingBearer}
                        onClick={() => saveBearer("MERCHANT")}
                        className={`text-left rounded-2xl border p-4 transition ${
                            deliveryBearer === "MERCHANT"
                                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                : "border-border hover:border-primary/40"
                        } disabled:opacity-60`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-bold text-sm">Seller (Merchant)</span>
                            {deliveryBearer === "MERCHANT" && (
                                <span className="text-[10px] uppercase tracking-wider font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">Active</span>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1.5">
                            The shop pays the rider out of its payout. The customer-paid portion of delivery is netted back,
                            so the shop only loses the free-delivery gap.
                        </p>
                    </button>
                </div>
            </div>

            {/* iter134q — Delivery-ETA display override */}
            <div className="mt-6 bg-card border border-border rounded-2xl p-5 max-w-3xl" data-testid="delivery-eta-section">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center shrink-0">
                        <Clock size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-sm">Estimated Delivery Time</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Controls the small <span className="font-semibold">clock pill</span> shown on restaurant cards,
                            the shop detail header and the order-tracking status.
                            Leave the default when your tenant delivers in minutes.
                            Pan-India tenants can switch to a text label like <span className="font-semibold">&quot;2 Days&quot;</span>
                            or hide the pill entirely.
                        </p>
                    </div>
                </div>

                <label className="mt-4 flex items-start gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        data-testid="delivery-eta-enabled"
                        checked={etaEnabled}
                        onChange={(e) => setEtaEnabled(e.target.checked)}
                        className="mt-1 accent-primary"
                    />
                    <span className="text-sm">
                        <span className="font-semibold">Show the estimated delivery time</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                            When off, the ETA pill disappears from every customer screen (restaurant cards, shop detail, order tracking).
                        </span>
                    </span>
                </label>

                {etaEnabled && (
                    <div className="mt-4 space-y-3">
                        <F label="Delivery estimate">
                            <select
                                data-testid="delivery-eta-preset"
                                className="inp"
                                value={etaPreset}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setEtaPreset(v);
                                    if (v === "__custom__") {
                                        // keep whatever custom text was already typed
                                    } else {
                                        setEtaText(v);
                                    }
                                }}
                            >
                                {DELIVERY_ETA_PRESETS.map((p) => (
                                    <option key={p.label} value={p.value}>{p.label}</option>
                                ))}
                                <option value="__custom__">Custom text…</option>
                            </select>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                <span className="font-semibold">Default</span> falls back to each shop&apos;s <code>avg_prep_minutes</code>
                                (e.g. &quot;25 mins&quot;). Presets and custom text override that value for every shop under this tenant.
                            </p>
                        </F>

                        {etaPreset === "__custom__" && (
                            <F label="Custom delivery text (max 60 characters)">
                                <input
                                    type="text"
                                    maxLength={60}
                                    data-testid="delivery-eta-custom"
                                    className="inp"
                                    value={etaText}
                                    onChange={(e) => setEtaText(e.target.value)}
                                    placeholder="e.g. 3-5 business days"
                                />
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    {etaText.length}/60 characters.
                                </p>
                            </F>
                        )}

                        <div className="rounded-xl bg-muted/40 border border-border px-3 py-2.5 text-xs">
                            <span className="text-muted-foreground">Preview:</span>{" "}
                            {etaText.trim() ? (
                                <span data-testid="delivery-eta-preview" className="inline-flex items-center gap-1 font-semibold">
                                    <Clock size={12} /> {etaText.trim()}
                                </span>
                            ) : (
                                <span data-testid="delivery-eta-preview" className="inline-flex items-center gap-1 font-semibold">
                                    <Clock size={12} /> 25 mins <span className="text-muted-foreground font-normal">(per-shop default)</span>
                                </span>
                            )}
                        </div>
                    </div>
                )}

                <button
                    type="button"
                    data-testid="save-delivery-eta"
                    onClick={saveEta}
                    disabled={savingEta || !etaLoaded}
                    className="mt-4 bg-primary text-primary-foreground rounded-xl px-5 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-60"
                >
                    {savingEta && <Loader2 size={16} className="animate-spin" />} Save Delivery ETA
                </button>
            </div>
            <style>{`.inp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.6rem 0.75rem;outline:none;font-size:0.875rem;width:100%}`}</style>
        </div>
    );
}

function F({ label, children }) {
    return (
        <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
                {label}
            </span>
            <div className="mt-1">{children}</div>
        </label>
    );
}

function ShareURLCard({ tenant }) {
    const [copied, setCopied] = useState(false);
    // The canonical public URL — uses the storefront origin + ?tenant=<slug>
    // so it works even when the tenant hasn't been pointed at a custom
    // subdomain yet. When a custom_domain is configured we surface it as
    // the recommended share link.
    const shareUrl = useMemo(() => {
        if (!tenant?.slug) return "";
        const origin = typeof window !== "undefined" ? window.location.origin : "";
        return `${origin}/?tenant=${tenant.slug}`;
    }, [tenant?.slug]);
    const customUrl = tenant?.custom_domain
        ? `https://${tenant.custom_domain.replace(/^https?:\/\//, "")}`
        : null;

    const copy = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Link copied — share it with anyone");
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Copy failed — please copy manually");
        }
    };

    if (!shareUrl) return null;
    return (
        <div className="mt-5 bg-primary/5 border border-primary/20 rounded-2xl p-4 max-w-3xl" data-testid="share-url-card">
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/15 text-primary inline-flex items-center justify-center flex-shrink-0">
                    <Globe size={18} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-primary">
                        Your tenant link
                    </p>
                    <p className="text-xs text-muted-foreground mb-2">
                        Share this URL with your customers, merchants and riders so they land directly on your app.
                    </p>
                    <div className="flex items-center gap-2 bg-card border border-border rounded-xl px-3 py-2.5 mb-2">
                        <code
                            className="flex-1 text-xs font-mono break-all text-foreground"
                            data-testid="tenant-share-url"
                        >{customUrl || shareUrl}</code>
                        <button
                            type="button"
                            onClick={() => copy(customUrl || shareUrl)}
                            data-testid="tenant-share-url-copy"
                            className="bg-primary text-primary-foreground rounded-lg px-2.5 py-1.5 text-xs font-bold inline-flex items-center gap-1.5"
                        >
                            {copied ? <Check size={12} /> : <Copy size={12} />}
                            {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                    {customUrl && (
                        <details className="text-[11px] text-muted-foreground">
                            <summary className="cursor-pointer">Also share the platform fallback link</summary>
                            <div className="flex items-center gap-2 bg-card border border-border rounded-xl px-3 py-2 mt-2">
                                <code className="flex-1 text-xs font-mono break-all">{shareUrl}</code>
                                <button
                                    type="button"
                                    onClick={() => copy(shareUrl)}
                                    className="text-primary text-xs font-bold inline-flex items-center gap-1"
                                ><Copy size={11} /> Copy</button>
                            </div>
                        </details>
                    )}
                    {!customUrl && (
                        <p className="text-[11px] text-muted-foreground">
                            Want a branded URL like <code>https://your-brand.com</code>? Ask the platform Super Admin to map your custom domain — they&apos;ll need a CNAME record on your DNS provider.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
